import { useEffect, useState } from "react";
import { Layers, Upload, ShieldCheck, FileText, CheckCircle2, History, ChevronDown, ChevronRight, Archive, AlertTriangle, Clock, User, Hash, Globe, Check, X, Inbox, Download, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useUser } from "@/context/UserContext";

const DEFAULT_ACCENT = { chip: "bg-[#4A3122]", bar: "from-[#B9772B] to-[#5B3A22]", text: "text-[#B9772B]" };

const PageHeader = ({ title, subtitle, icon: Icon, accent = DEFAULT_ACCENT }) => (
  <div className="mb-6">
    <div className={`h-1.5 w-16 rounded-full bg-gradient-to-r ${accent.bar} mb-4`} />
    <div className="flex items-center gap-3 mb-2">
      {Icon && (
        <span className={`w-9 h-9 rounded-xl ${accent.chip} flex items-center justify-center shadow-sm`}>
          <Icon className="w-5 h-5 text-white" />
        </span>
      )}
      <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#B9772B]">Zoetwaren Kompas</span>
    </div>
    <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#2B211A]">{title}</h1>
    <p className="text-[#6E5C4A] mt-2 max-w-2xl">{subtitle}</p>
  </div>
);

const CAT = {
  wet: { label: "Wetgeving", cls: "bg-slate-900 text-white" },
  cao: { label: "Cao", cls: "bg-blue-600 text-white" },
  functiehandboek: { label: "Functiehandboek", cls: "bg-teal-600 text-white" },
  wor: { label: "WOR", cls: "bg-indigo-600 text-white" },
};

const CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB per chunk
const fmtBytes = (n) => {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
};

const UploadDialog = ({ onDone }) => {
  const [file, setFile] = useState(null);
  const [naam, setNaam] = useState("");
  const [cat, setCat] = useState("");
  const [versie, setVersie] = useState("");
  const [eigenaar, setEigenaar] = useState("");
  const [ingang, setIngang] = useState("");
  const [eind, setEind] = useState("");
  const [prioriteit, setPrioriteit] = useState("");
  const [alsConcept, setAlsConcept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [open, setOpen] = useState(false);

  const reset = () => {
    setFile(null); setNaam(""); setCat(""); setVersie(""); setEigenaar("");
    setIngang(""); setEind(""); setPrioriteit(""); setAlsConcept(false); setProgress(0);
  };

  const submit = async () => {
    if (!file || !naam || !cat || !versie) { toast.error("Vul de verplichte velden in en kies een bestand."); return; }
    setBusy(true); setProgress(0);
    const status = alsConcept ? "concept" : "actief";
    const meta = {
      documentnaam: naam, broncategorie: cat, versie,
      bestandsnaam: file.name, totale_grootte: file.size,
      ingangsdatum: ingang || null, einddatum: eind || null,
      eigenaar: eigenaar || null,
      prioriteit: prioriteit ? parseInt(prioriteit, 10) : null,
      status,
    };
    try {
      const initRes = await api.post("/documents/upload/init", meta);
      const uploadId = initRes.data.upload_id;
      const totaal = Math.ceil(file.size / CHUNK_SIZE) || 1;
      for (let i = 0; i < totaal; i++) {
        const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", i);
        fd.append("chunk", blob, file.name);
        await api.post("/documents/upload/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setProgress(Math.round(((i + 1) / totaal) * 100));
      }
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      const res = await api.post("/documents/upload/complete", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(res.data.melding);
      setOpen(false); reset();
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload mislukt.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="open-upload-btn" variant="outline" className="border-slate-300">
          <Upload className="w-4 h-4 mr-2" /> Bron toevoegen / vervangen
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Bronbestand uploaden</DialogTitle>
          <DialogDescription>Voeg een nieuwe of vervangende officiële bron toe. Het originele bestand wordt bewaard; de inhoud wordt intern verwerkt en geïndexeerd. Bronbestanden worden nooit overschreven — elke upload is een nieuwe versie.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-sm">Documentnaam *</Label>
              <Input data-testid="upload-naam" value={naam} onChange={(e) => setNaam(e.target.value)} placeholder="Bijv. Cao Zoetwaren 2024-2026" /></div>
            <div><Label className="text-sm">Broncategorie *</Label>
              <Select value={cat} onValueChange={setCat}>
                <SelectTrigger data-testid="upload-categorie"><SelectValue placeholder="Kies categorie" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="wet">Wetgeving</SelectItem>
                  <SelectItem value="cao">Cao</SelectItem>
                  <SelectItem value="functiehandboek">Functiehandboek</SelectItem>
                  <SelectItem value="wor">WOR</SelectItem>
                </SelectContent>
              </Select></div>
            <div><Label className="text-sm">Versie *</Label>
              <Input data-testid="upload-versie" value={versie} onChange={(e) => setVersie(e.target.value)} placeholder="Bijv. 2024-2026" /></div>
            <div><Label className="text-sm">Eigenaar</Label>
              <Input data-testid="upload-eigenaar" value={eigenaar} onChange={(e) => setEigenaar(e.target.value)} placeholder="Standaard: uw e-mail" /></div>
            <div><Label className="text-sm">Prioriteit (retrieval)</Label>
              <Input data-testid="upload-prioriteit" type="number" min="1" max="9" value={prioriteit} onChange={(e) => setPrioriteit(e.target.value)} placeholder="Standaard uit categorie" /></div>
            <div><Label className="text-sm">Geldig vanaf</Label>
              <Input data-testid="upload-ingang" type="date" value={ingang} onChange={(e) => setIngang(e.target.value)} /></div>
            <div><Label className="text-sm">Geldig tot</Label>
              <Input data-testid="upload-eind" type="date" value={eind} onChange={(e) => setEind(e.target.value)} /></div>
          </div>
          <div><Label className="text-sm">Bestand (PDF, DOCX of TXT) *</Label>
            <Input data-testid="upload-file" type="file" accept=".pdf,.docx,.txt" onChange={(e) => setFile(e.target.files[0])} />
            {file && <p data-testid="upload-bestandsgrootte" className="text-xs text-slate-500 mt-1">{file.name} · {fmtBytes(file.size)}</p>}
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input data-testid="upload-concept" type="checkbox" checked={alsConcept} onChange={(e) => setAlsConcept(e.target.checked)} className="rounded border-slate-300" />
            Opslaan als <span className="font-medium">concept</span> (nog niet activeren of indexeren)
          </label>
          <p className="text-xs text-slate-500">De inhoud wordt uitsluitend intern gebruikt voor vraag &amp; antwoord en de checks. Bij activeren worden oude versies gearchiveerd (auditdoeleinden) en wordt de zoekindex opnieuw opgebouwd.</p>
          {busy && progress > 0 && (
            <div data-testid="upload-progress" className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
              <div className="h-full bg-blue-600 transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="submit-upload-btn" onClick={submit} disabled={busy} className="bg-slate-900">
            {busy ? (progress < 100 ? `Uploaden… ${progress}%` : "Verwerken…") : (alsConcept ? "Opslaan als concept" : "Verwerken & indexeren")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleString("nl-NL", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const STATUS_STYLE = {
  Actief: { cls: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  Concept: { cls: "bg-amber-100 text-amber-800", icon: Clock },
  Gearchiveerd: { cls: "bg-slate-200 text-slate-600", icon: Archive },
  Vervallen: { cls: "bg-red-100 text-red-700", icon: X },
};

const downloadBron = async (docId, bestandsnaam) => {
  try {
    const res = await api.get(`/documents/${docId}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = bestandsnaam || `${docId}`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(e.response?.status === 404 ? "Voor deze bron is geen origineel bestand beschikbaar." : "Download mislukt.");
  }
};

const VersieRij = ({ v, isSuper, onChange }) => {
  const st = STATUS_STYLE[v.status] || STATUS_STYLE.Gearchiveerd;
  const StIcon = st.icon;
  const activeer = async () => {
    try { const r = await api.post(`/documents/${v.doc_id}/activate`); toast.success(r.data.melding); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Activeren mislukt."); }
  };
  const verwijder = async () => {
    if (!window.confirm(`Weet u zeker dat u '${v.documentnaam}' versie ${v.versie} wilt verwijderen? De bron wordt eerst gearchiveerd en op 'vervallen' gezet; het bestand blijft bewaard.`)) return;
    try { const r = await api.delete(`/documents/${v.doc_id}`); toast.success(r.data.melding); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Verwijderen mislukt."); }
  };
  return (
    <div data-testid={`history-version-${v.doc_id}`} className="relative pl-6 pb-5 last:pb-0">
      <span className={`absolute left-0 top-1.5 w-3 h-3 rounded-full ring-4 ring-white ${v.actief ? "bg-emerald-500" : "bg-slate-300"}`} />
      <div className="border border-slate-200 rounded-lg p-4 bg-white">
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <p className="font-heading font-semibold text-slate-900">Versie {v.versie}</p>
          <span data-testid={`version-status-${v.doc_id}`}
            className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${st.cls}`}>
            <StIcon className="w-3 h-3" /> {v.status}
          </span>
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
          <div className="flex justify-between"><dt className="text-slate-500">Uploaddatum</dt><dd className="font-medium text-slate-800">{fmtDateTime(v.uploaddatum)}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-500">Geldig vanaf</dt><dd className="font-medium text-slate-800">{v.ingangsdatum || "—"}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-500">Geldig tot</dt><dd className="font-medium text-slate-800">{v.einddatum ? fmtDateTime(v.einddatum) : "—"}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-500">Prioriteit</dt><dd className="font-medium text-slate-800">{v.bronprioriteit ?? "—"}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-500 flex items-center gap-1"><Hash className="w-3 h-3" />Fragmenten</dt><dd className="font-medium text-slate-800">{v.aantal_fragmenten}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-500 flex items-center gap-1"><User className="w-3 h-3" />Eigenaar</dt><dd className="font-medium text-slate-800 truncate max-w-[60%]">{v.eigenaar || v.geupload_door || "—"}</dd></div>
        </dl>
        <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between gap-2 flex-wrap text-xs">
          <span className="text-slate-400">Bestand: {v.heeft_bestand ? <span className="text-slate-600">{v.bestandsnaam} · {fmtBytes(v.bestandsgrootte)}</span> : "geen origineel bewaard"}</span>
          <div className="flex items-center gap-2">
            {v.heeft_bestand && (
              <Button data-testid={`download-bron-${v.doc_id}`} onClick={() => downloadBron(v.doc_id, v.bestandsnaam)} size="sm" variant="outline" className="h-7 border-slate-300 text-slate-700">
                <Download className="w-3.5 h-3.5 mr-1" /> Download origineel
              </Button>
            )}
            {v.status === "Concept" && (
              <Button data-testid={`activeer-bron-${v.doc_id}`} onClick={activeer} size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Activeren
              </Button>
            )}
            {isSuper && v.status !== "Vervallen" && (
              <Button data-testid={`verwijder-bron-${v.doc_id}`} onClick={verwijder} size="sm" variant="outline" className="h-7 border-red-200 text-red-700 hover:bg-red-50">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Verwijderen
              </Button>
            )}
          </div>
        </div>
        {v.gebruikt_in_auditlogs > 0 && (
          <div className="mt-2 text-xs">
            <span data-testid={`version-audit-${v.doc_id}`} className="inline-flex items-center gap-1 text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
              <Clock className="w-3 h-3" /> Aangehaald in {v.gebruikt_in_auditlogs} auditlog{v.gebruikt_in_auditlogs > 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const BronHistorie = ({ groep, idx, isSuper, onChange }) => {
  const [open, setOpen] = useState(idx === 0);
  const c = CAT[groep.broncategorie] || { label: groep.broncategorie, cls: "bg-slate-400 text-white" };
  return (
    <div data-testid={`bron-history-${idx}`} className="border border-slate-200 rounded-xl bg-white overflow-hidden">
      <button data-testid={`history-toggle-${idx}`} onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 p-4 hover:bg-slate-50 transition-colors text-left">
        <div className="flex items-center gap-3 min-w-0">
          {open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${c.cls} shrink-0`}>{c.label}</span>
          <span className="font-heading font-semibold text-slate-900 truncate">{groep.documentnaam}</span>
        </div>
        <span className="text-xs text-slate-500 shrink-0">{groep.aantal_versies} versie{groep.aantal_versies > 1 ? "s" : ""}</span>
      </button>
      {open && (
        <div className="px-4 pb-4">
          {!groep.heeft_actieve_versie && (
            <div data-testid={`history-no-active-${idx}`} className="mb-3 flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-2.5">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> Geen actieve versie voor deze bron. Er wordt momenteel geen inhoud van deze bron gebruikt.
            </div>
          )}
          {groep.meerdere_actieve_versies && (
            <div data-testid={`history-multiple-active-${idx}`} className="mb-3 flex items-start gap-2 text-sm bg-red-50 border border-red-200 text-red-800 rounded-md p-2.5">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> Let op: er zijn meerdere actieve versies. Controleer het versiebeheer — normaliter is er precies één actieve versie.
            </div>
          )}
          <div className="relative border-l-2 border-slate-100 ml-1.5 pt-1">
            {groep.versies.map((v) => <VersieRij key={v.doc_id} v={v} isSuper={isSuper} onChange={onChange} />)}
          </div>
        </div>
      )}
    </div>
  );
};

const STATUS_FILTERS = ["Actief", "Concept", "Gearchiveerd", "Vervallen", "Alle"];

const Versiehistorie = ({ isSuper, reloadKey, onChange }) => {
  const [groepen, setGroepen] = useState(null);
  const [filter, setFilter] = useState("Actief");
  const load = () => api.get("/documents/history").then((r) => setGroepen(r.data)).catch(() => setGroepen([]));
  useEffect(() => { load(); }, [reloadKey]);
  const handleChange = () => { load(); onChange?.(); };

  const gefilterd = (groepen || [])
    .map((g) => ({ ...g, versies: filter === "Alle" ? g.versies : g.versies.filter((v) => v.status === filter) }))
    .filter((g) => g.versies.length > 0);

  return (
    <div data-testid="versiehistorie-section" className="mt-10">
      <div className="flex items-center gap-2 mb-1">
        <History className="w-5 h-5 text-slate-700" />
        <h2 className="font-heading text-xl font-bold text-slate-900">Versiehistorie</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4 max-w-2xl">Per bron zie je alle versies (Concept, Actief, Gearchiveerd, Vervallen) met uploaddatum, status, eigenaar en het originele bestand. Bronbestanden worden nooit overschreven — elke wijziging is een nieuwe versie. Gearchiveerde en vervallen versies blijven bewaard zodat historische antwoorden en auditlogs reproduceerbaar blijven.</p>
      <div data-testid="history-status-filter" className="flex items-center gap-1.5 mb-4 flex-wrap">
        <span className="text-xs font-medium text-slate-500 mr-1">Status:</span>
        {STATUS_FILTERS.map((s) => (
          <button key={s} data-testid={`history-filter-${s.toLowerCase()}`} onClick={() => setFilter(s)}
            className={`text-xs font-medium px-3 py-1 rounded-full border transition-colors ${filter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
            {s}
          </button>
        ))}
      </div>
      {groepen === null ? (
        <p className="text-sm text-slate-400">Versiehistorie laden…</p>
      ) : gefilterd.length === 0 ? (
        <p data-testid="history-empty" className="text-sm text-slate-400">
          {filter === "Alle" ? "Nog geen bronversies beschikbaar." : `Geen bronnen met status "${filter}".`}
        </p>
      ) : (
        <div className="space-y-3">
          {gefilterd.map((g, i) => <BronHistorie key={g.documentnaam} groep={g} idx={i} isSuper={isSuper} onChange={handleChange} />)}
        </div>
      )}
    </div>
  );
};

const ExterneBronnen = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/external-sources").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  if (items === null || items.length === 0) return null;
  return (
    <div data-testid="externe-bronnen-goedgekeurd" className="mt-10">
      <div className="flex items-center gap-2 mb-1">
        <Globe className="w-5 h-5 text-slate-700" />
        <h2 className="font-heading text-xl font-bold text-slate-900">Goedgekeurde externe bronnen</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4 max-w-2xl">Officiële externe bronnen die door de beheerder zijn goedgekeurd. Alleen de verwijzing (titel en link) wordt getoond; de inhoud wordt niet opgeslagen.</p>
      <ul className="space-y-2">
        {items.map((b) => (
          <li key={b.id} data-testid={`externe-bron-goedgekeurd-${b.id}`} className="bg-white border border-slate-200 rounded-lg p-3 flex items-start gap-2.5">
            <Globe className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
            <div className="min-w-0">
              <a href={b.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-blue-700 hover:underline break-words">{b.naam}</a>
              <p className="text-xs text-slate-400 break-all">{b.url}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ExterneBronnenWachtrij = () => {
  const [items, setItems] = useState(null);
  const load = () => api.get("/external-sources/pending").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const beoordeel = async (id, actie) => {
    try {
      await api.post(`/external-sources/${id}/${actie}`);
      toast.success(actie === "approve" ? "Externe bron goedgekeurd." : "Externe bron afgewezen.");
      load();
    } catch (e) { toast.error("Actie mislukt."); }
  };

  return (
    <div data-testid="externe-bronnen-wachtrij" className="mt-10">
      <div className="flex items-center gap-2 mb-1">
        <Inbox className="w-5 h-5 text-slate-700" />
        <h2 className="font-heading text-xl font-bold text-slate-900">Wachtrij externe bronnen</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4 max-w-2xl">Externe bronnen die zijn geraadpleegd bij vraag &amp; antwoord en wachten op beoordeling. Keur goed om ze in de Bronnen-lijst te tonen, of wijs ze af.</p>
      {items === null ? (
        <p className="text-sm text-slate-400">Wachtrij laden…</p>
      ) : items.length === 0 ? (
        <p data-testid="wachtrij-leeg" className="text-sm text-slate-400">Geen externe bronnen in de wachtrij.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((b) => (
            <li key={b.id} data-testid={`wachtrij-item-${b.id}`} className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <a href={b.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-blue-700 hover:underline break-words">{b.naam}</a>
                <p className="text-xs text-slate-400 break-all">{b.url}</p>
                {b.eerste_vraag && <p className="text-xs text-slate-400 mt-0.5">Bij vraag: “{b.eerste_vraag}”</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button data-testid={`wachtrij-approve-${b.id}`} onClick={() => beoordeel(b.id, "approve")} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                  <Check className="w-4 h-4 mr-1" /> Goedkeuren
                </Button>
                <Button data-testid={`wachtrij-reject-${b.id}`} onClick={() => beoordeel(b.id, "reject")} size="sm" variant="outline" className="border-slate-300 text-slate-700">
                  <X className="w-4 h-4 mr-1" /> Afwijzen
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Bronnen = () => {
  const [docs, setDocs] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const { user } = useUser();
  const isAdmin = user?.rol === "beheerder" || user?.rol === "superbeheerder";
  const isSuper = user?.rol === "superbeheerder";
  const loadDocs = () => api.get("/documents").then((r) => setDocs(r.data));
  const refreshAll = () => { loadDocs(); setReloadKey((k) => k + 1); };
  useEffect(() => { loadDocs(); }, []);

  return (
    <div className="animate-fade-up">
      <PageHeader title="Bronnen & versies" icon={Layers}
        accent={{ chip: "bg-blue-600", bar: "from-blue-400 to-blue-700", text: "text-blue-600" }}
        subtitle="De kernbronnen worden uitsluitend intern gebruikt om vragen te beantwoorden en checks uit te voeren. Hier zie je uitsluitend welke bronnen actief zijn en welke versie wordt gehanteerd — de volledige documenten zijn niet openbaar te doorbladeren." />

      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5">
          <ShieldCheck className="w-4 h-4" /> Bronvermelding en versiebeheer actief · bronhiërarchie gehandhaafd
        </div>
        {isAdmin ? (
          <UploadDialog onDone={refreshAll} />
        ) : (
          <span data-testid="upload-admin-only" className="text-xs text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-3 py-1.5">
            Bronnen kunnen uitsluitend door de beheerder worden toegevoegd of vervangen.
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {docs.map((d) => {
          const c = CAT[d.broncategorie] || { label: d.broncategorie, cls: "bg-slate-400 text-white" };
          return (
            <div key={d.id} data-testid={`document-${d.id}`} className="bg-white border border-slate-200 rounded-xl p-5 hover:border-slate-400 hover:shadow-sm transition-all relative overflow-hidden">
              <div className="absolute top-0 left-0 h-full w-1.5 bg-gradient-to-b from-blue-400 to-blue-700" />
              <div className="flex items-start justify-between gap-2 mb-3">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${c.cls}`}>{c.label}</span>
                <span className="text-[11px] text-slate-500 flex items-center gap-1">
                  <span className="w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center">{d.bronprioriteit}</span>
                  prioriteit
                </span>
              </div>
              <p className="font-heading font-semibold text-slate-900 text-lg leading-tight flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-400 shrink-0" /> {d.documentnaam}
              </p>
              <dl className="mt-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><dt className="text-slate-500">Versie</dt><dd className="font-medium text-slate-800">{d.versie}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Ingangsdatum</dt><dd className="font-medium text-slate-800">{d.ingangsdatum || "—"}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Einddatum</dt><dd className="font-medium text-slate-800">{d.einddatum || "—"}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Doorzoekbare artikelen</dt><dd className="font-medium text-slate-800">{d.fragmenten}</dd></div>
              </dl>
              {d.actief && (
                <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Actieve versie
                  </span>
                  {isAdmin && d.heeft_bestand && (
                    <Button data-testid={`card-download-${d.id}`} onClick={() => downloadBron(d.id, d.original_filename)} size="sm" variant="outline" className="h-7 border-slate-300 text-slate-700">
                      <Download className="w-3.5 h-3.5 mr-1" /> Origineel
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isAdmin && <Versiehistorie isSuper={isSuper} reloadKey={reloadKey} onChange={loadDocs} />}

      <ExterneBronnen />
      {isAdmin && <ExterneBronnenWachtrij />}
    </div>
  );
};

export default Bronnen;
export { PageHeader };
